import { useEffect, type RefObject } from "react";
import type { ResolvedShowcaseMedia } from "../../lib/showcase-media";

export function useShowcasePlayback(rootRef: RefObject<HTMLDivElement | null>, media: ResolvedShowcaseMedia) {
  // Source changes replace keyed video nodes. Bind the new nodes, while leaving
  // playback alone when only image URLs or query refresh state change.
  const sources = [media.homeHero, ...media.homeVideos, ...media.accordionVideos].map(video => video.src).join("\n");
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const videos = Array.from(root.querySelectorAll<HTMLVideoElement>("video[data-showcase-video],video[data-gallery-video]"));
    const autoplayVideos = videos.filter(video => video.hasAttribute("data-showcase-video"));
    let hovered: HTMLVideoElement | null = null;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const start = (video: HTMLVideoElement) => { void video.play().catch(() => { /* Autoplay may be denied by the browser. Keep the poster visible. */ }); };
    const muteAll = () => videos.forEach(video => { video.muted = true; });
    function enableSound() {
      muteAll();
      if (!hovered || document.hidden || !navigator.userActivation?.hasBeenActive) return;
      const current = hovered;
      current.muted = false;
      if (!current.hasAttribute("data-showcase-video")) return;
      void current.play().catch(() => { current.muted = true; start(current); });
    }
    videos.forEach(video => {
      video.muted = true;
      const surface = video.closest<HTMLElement>("[data-video-surface]") || video;
      surface.addEventListener("pointerenter", event => {
        if ((event as PointerEvent).pointerType === "touch") return;
        hovered = video;
        enableSound();
      }, options);
      surface.addEventListener("pointerleave", () => {
        if (hovered === video) { hovered = null; muteAll(); }
      }, options);
      const trigger: HTMLElement | null = video.hasAttribute("data-gallery-video") ? surface.querySelector<HTMLButtonElement>("button") : video;
      surface.addEventListener("click", event => {
        if ((event.target as Element).closest("a,button") && event.target !== trigger && !trigger?.contains(event.target as Node)) return;
        hovered = video;
        enableSound();
      }, options);
      trigger?.addEventListener("keydown", event => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        if (!video.muted) { hovered = null; muteAll(); }
        else { hovered = video; enableSound(); }
      }, options);
      trigger?.addEventListener("blur", () => { if (hovered === video) { hovered = null; muteAll(); } }, options);
      if (video.hasAttribute("data-showcase-video")) {
        video.addEventListener("canplay", () => { if (!document.hidden) start(video); }, options);
        start(video);
      }
    });
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting && entry.target === hovered) { hovered = null; muteAll(); }
    }));
    videos.forEach(video => observer.observe(video));
    document.addEventListener("visibilitychange", () => {
      hovered = null;
      muteAll();
      autoplayVideos.forEach(video => document.hidden ? video.pause() : start(video));
    }, options);
    return () => { controller.abort(); observer.disconnect(); muteAll(); videos.forEach(video => video.pause()); };
  }, [rootRef, sources]);
}
